import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Measure from "react-measure";
import { useDispatch } from "react-redux";

import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";

import { DTrees } from "reducers/DTrees";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { TextOverflow } from "utils/common";
import { MIN_GRID_HEIGHT, DEFAULT_ROW_HEIGHT } from "utils/constants";
import { useGridScrollUtils } from "utils/DataGrid";
import DateCell from "widgets/grid/cells/DateColumn";
import BasicCheck from "widgets/grid/cells/BasicCheck";
import DTreeActionsCell from "widgets/grid/cells/DTreeActions";
import StatusColumn from "widgets/grid/cells/StatusColumn";
import { getActionMetadata } from "widgets/grid/cells/DTreeActions.js";
import { DisabledDTreeRowRenderer } from "utils/columns/DisabledRow";

const DTREE_STATUS_MAP = {
	draft: {
		label: "Draft",
		labelClass: "draft",
	},
	published: {
		label: "Published",
		labelClass: "published",
	},
	["post-publish-edit"]: {
		label: "Published (Edited)",
		labelClass: "post-publish-edit",
	},
	unknown:{
		label:"unknown",
		labelClass:"in-unknown in-production",
	},
};

const columns = [
	{
		key: "name",
		name: "Decision Tree Name",
		width: 319,
		formatter: TextOverflow,
		sortable: true,
	},
	{
		key: "id",
		name: "ID",
		width: 85,
		cellClass: "centered",
		sortable: true,
	},
	{
		key: "status",
		name: "Status",
		width: 140,
		cellClass: "centered",
		formatter: StatusColumn,
		sortable: true,
		getRowMetaData: () => {
			return { statusMap: DTREE_STATUS_MAP };
		},
	},
	{
		key: "assigned",
		name: "Assigned",
		width: 74,
		cellClass: "centered",
		formatter: BasicCheck,
		sortable: true,
	},
	{
		...DTreeActionsCell,
	},
	{
		key: "versionsInUse",
		name: "Versions In Use",
		width: 114,
		cellClass: "centered",
		formatter: ({ value }) => { return <div>{value.length}</div>; },
	},
	{
		key: "linkedCreatives",
		name: "Linked Creatives",
		width: 119,
		cellClass: "centered",
		formatter: (props) => {
			let { value, dependentValues } = props;
			let { actions: { openLinkedCreativesModal }, ...dtree } = dependentValues || {};

			if(value.length){
				let clickHandler = () => { openLinkedCreativesModal(dtree); };
				return <a className="modal-nav-link" onClick={clickHandler}>{value.length}</a>;
			}

			return <span className="disabled">0</span>;
		},
		getRowMetaData: getActionMetadata,
	},
	// {
	// 	key: "lastPublished",
	// 	name: "Last Published",
	// 	width: 124,
	// },
	{
		...DateCell,
		key: "lastUpdated",
		name: "Last Modified",
		sortable: true,
	}
];

export default function Grid({
	dtrees,
	isLoaded,
	creativeId,
	libraryId,
	actions,
	sort,
}){
	const dataGridRef = useGridScrollUtils([dtrees]);
	const dispatch = useDispatch()
	 , mappedDTrees = useMemo(() => {
			return dtrees.map((dtree) => {
				return {
					...dtree,
					actions,
					libraryId,
					creativeId,
					isDisabled: !dtree.assignable,
				};
			});
		}, [dtrees, actions]);

	const [sortColumn, sortDirection] = sort.split(":");

	function getRow(rowId){
		return mappedDTrees[rowId];
	}

	let gridProps = {
		columns,
		rowGetter: getRow,
		rowsCount: dtrees.length,
		rowHeight: DEFAULT_ROW_HEIGHT,
		creativeId,
		rowRenderer: DisabledDTreeRowRenderer,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(DTrees.setSort(col, dir)),
	};

	useTooltipRebuild(mappedDTrees);

	return (
		<Measure>
			{(obj) => {
				let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
				return (
					<div className="measure-component" ref={obj.measureRef}>
						<ProgressIndicator isLoading={!isLoaded} />
						<div ref={dataGridRef}>
							<DataGrid
								{...gridProps}
								minHeight={gridHeight}
							/>
						</div>
					</div>
				);
			}}
		</Measure>
	);
}

Grid.displayName = "DTreeGrid";

Grid.defaultProps = {
	dtrees: [],
	isLoaded: false,
	actions: {},
};

Grid.propTypes = {
	dtrees: PropTypes.array,
	isLoaded: PropTypes.bool,
	actions: PropTypes.object,
};
